import React, { useEffect, useState } from "react";
import type { StockNewsItemData } from "@crypto-trading/common/browser";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import type { LivePriceExchangeCode } from "@/hooks/useTokenLivePrice";
import { useInView } from "@/hooks/useInView";

interface StockNewsTileProps {
  item: StockNewsItemData;
  onDetails: () => void;
  onTrade?: () => void;
}

function formatPrice(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return "Not available";
  return `$${price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatTimeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function excerpt(body: string, title: string): string {
  const text = body.trim();
  if (!text || text === title.trim()) return "";
  if (text.length <= 180) return text;
  return `${text.slice(0, 177).trimEnd()}...`;
}

export const StockNewsTile: React.FC<StockNewsTileProps> = ({
  item,
  onDetails,
  onTrade,
}) => {
  const matched = Boolean(item.primaryTicker);
  const [tileRef, isInView] = useInView<HTMLElement>({
    rootMargin: "50px",
    threshold: 0.1,
  });
  const liveExchange = (item.alertPriceExchange ||
    "dynamic") as LivePriceExchangeCode;
  const { price: livePrice, isSubscribed } = useTokenLivePrice(
    matched ? item.priceSymbol : null,
    liveExchange,
    matched && isInView
  );
  const [priceLoadTimeout, setPriceLoadTimeout] = useState(false);

  useEffect(() => {
    if (!matched || !isInView || livePrice) {
      setPriceLoadTimeout(false);
      return;
    }
    const timer = setTimeout(() => setPriceLoadTimeout(true), 8000);
    return () => clearTimeout(timer);
  }, [matched, isInView, livePrice]);

  const priceLoading = matched && isInView && !livePrice && !priceLoadTimeout;
  const change =
    item.alertPrice && livePrice
      ? ((livePrice - item.alertPrice) / item.alertPrice) * 100
      : null;
  const description = excerpt(item.body, item.title);
  const sourceLabel = item.sourceName || item.source || "Stocks";

  return (
    <article
      ref={tileRef}
      className="rounded-xl border border-sky-500/30 bg-secondary p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-300">
            {sourceLabel}
            <span className="ml-2 font-normal normal-case tracking-normal text-text-secondary">
              {formatTimeAgo(item.publishedAt)}
            </span>
          </p>
          <h3 className="mt-1 text-base font-semibold leading-snug text-text-primary">
            {item.title}
          </h3>
        </div>
        {matched && (
          <span className="shrink-0 rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-bold text-sky-200">
            {item.primaryTicker}
          </span>
        )}
      </div>

      {matched ? (
        <div className="mt-3 grid grid-cols-2 gap-3 border-t border-sky-500/20 pt-3">
          <div>
            <p className="text-xs text-text-secondary">Alert price</p>
            <p className="text-sm text-text-primary">
              {formatPrice(item.alertPrice)}
            </p>
          </div>
          <div>
            <p className="text-xs text-text-secondary">Current price</p>
            {priceLoading ? (
              <div className="mt-1 h-5 w-20 animate-pulse rounded bg-gray-600/30" />
            ) : (
              <p className="text-sm text-text-primary">
                {formatPrice(livePrice)}
                {isSubscribed && livePrice ? (
                  <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
                ) : null}
              </p>
            )}
            {change != null && (
              <p
                className={`text-xs ${change >= 0 ? "text-green-400" : "text-red-400"}`}
              >
                {change >= 0 ? "+" : ""}
                {change.toFixed(2)}%
              </p>
            )}
          </div>
        </div>
      ) : (
        description && (
          <p className="mt-2 text-sm leading-relaxed text-text-secondary">
            {description}
          </p>
        )
      )}

      {item.matches.length > 1 && (
        <p className="mt-2 text-xs text-text-secondary">
          Also mentions{" "}
          {item.matches
            .map((match) => match.ticker)
            .filter((ticker) => ticker !== item.primaryTicker)
            .join(", ")}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onDetails}
          className="flex-1 rounded-lg border border-sky-500/40 py-2 text-sm font-medium text-text-primary hover:bg-sky-500/10"
        >
          Details
        </button>
        {matched && onTrade && (
          <button
            type="button"
            onClick={onTrade}
            className="flex-1 rounded-lg bg-accent py-2 text-sm font-semibold text-white hover:bg-accent/80"
          >
            Trade
          </button>
        )}
      </div>
    </article>
  );
};
